import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
  Observable,
  of,
  from,
  throwError,
  combineLatest,
  BehaviorSubject,
} from "rxjs";
import {
  delay,
  map,
  switchMap,
  catchError,
  take,
  tap,
  distinctUntilChanged,
  filter,
} from "rxjs/operators";
import { environment } from "@environments/environment";
import { AuthService } from "./auth.service";
import { JobMatch } from "@models/job-match.interface";
import { Job, JobLocation } from "../types/job.types";
import { WorkType } from "../types/work-type.enum";
import { SupabaseService } from "./supabase.service";
import { JobMatchingAIService } from "./job-matching-ai.service";
import { ProfileDetails } from "@models/profile.interface";

// Type alias for casting if needed frequently
type EnvironmentType = typeof environment;

export interface FindMoreJobMatchesResult {
  scoredMatches: JobMatch[];
  sourceTotallyExhausted: boolean;
}

export interface FetchCandidateJobsResult {
  candidateBatch: Job[];
  totalPotentialCount: number; // Add total potential count
  sourceTotallyExhausted: boolean;
}

@Injectable({
  providedIn: "root",
})
export class JobMatcherService {
  private readonly API_URL = (environment as EnvironmentType).apiUrl || "";

  // Flag to use mock data in development
  private useMockData = false;
  private openAiProcessedCount = 0; // Counter for OpenAI processed jobs

  // Add observable for job matches count
  private _jobMatchesCount = new BehaviorSubject<number>(0);
  jobMatchesCount$ = this._jobMatchesCount.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private supabaseService: SupabaseService,
    private jobMatchingAIService: JobMatchingAIService
  ) {
    // Initialize job matches count when service is created
    this.initializeJobMatchesCount();

    // Subscribe to auth changes to update count
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.updateJobMatchesCount();
      } else {
        this._jobMatchesCount.next(0);
      }
    });
  }

  /**
   * Initialize the job matches count
   */
  private async initializeJobMatchesCount(): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (user) {
      await this.updateJobMatchesCount();
    }
  }

  /**
   * Create a new matching session
   */
  createMatchingSession(): Observable<{
    session_id: string;
    firstJob: JobMatch;
  }> {
    const headers = this.getAuthHeaders();

    return this.http.post<{ session_id: string; firstJob: JobMatch }>(
      `${this.API_URL}/ai-matcher/sessions`,
      {},
      { headers }
    );
  }

  /**
   * Update the job matches count observable
   */
  async updateJobMatchesCount(): Promise<void> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      this._jobMatchesCount.next(0);
      return;
    }

    try {
      const { count, error } = await this.supabaseService
        .getClient()
        .from("job_matches")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("status", "presented");

      if (error) {
        console.error("Error fetching job matches count:", error);
        throw error;
      }

      this._jobMatchesCount.next(count || 0);
    } catch (err) {
      console.error("Error updating job matches count:", err);
      this._jobMatchesCount.next(0);
    }
  }

  /**
   * Get available job matches for the current user
   */
  async getAvailableJobMatches(userId: string): Promise<JobMatch[]> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from("job_matches")
        .select(
          `
          match_id,
          job_id,
          user_id,
          ai_score,
          ai_reasoning,
          status,
          created_at,
          updated_at,
          jobs!inner ( *, job_locations(*) )
        `
        )
        .eq("user_id", userId)
        .eq("status", "presented")
        .eq("jobs.status", "published") // Only show matches for published jobs
        .order("ai_score", { ascending: false });

      if (error) {
        console.error("Error fetching available job matches:", error);
        throw error;
      }

      // Map the data to JobMatch[] format
      const matches: JobMatch[] = (data || []).map((match: any) => {
        const job = match.jobs as Job; // Job now includes job_locations
        if (job) {
          job.display_location = this.formatDisplayLocation(job);
        }
        return {
          match_id: match.match_id,
          job_id: match.job_id,
          user_id: match.user_id,
          session_id: `session-${match.user_id}-${match.job_id}`, // Consider if session_id is needed here or generated elsewhere
          ai_score: match.ai_score,
          ai_reasoning: match.ai_reasoning,
          status: match.status,
          created_at: match.created_at,
          updated_at: match.updated_at,
          job: job, // job now has display_location populated
        };
      });

      return matches;
    } catch (err) {
      console.error("Error in getAvailableJobMatches:", err);
      return [];
    }
  }

  /**
   * Find more job matches by calling the findAndScoreNextBatch method
   */
  findMoreJobMatches(): Observable<FindMoreJobMatchesResult> {
    return this.getProfileAndResumeText().pipe(
      switchMap(({ profile, resumeText, currentOffset }) => {
        if (!profile || !resumeText) {
          // If no profile/resume, source is effectively exhausted for matching
          return of({ scoredMatches: [], sourceTotallyExhausted: true });
        }

        return this.findAndScoreNextBatch(profile, resumeText, currentOffset);
      }),
      catchError((err) => {
        console.error("Error finding more job matches:", err);
        // Assume exhaustion on error to prevent potential loops
        return of({ scoredMatches: [], sourceTotallyExhausted: true });
      })
    );
  }

  /**
   * Helper method to get profile details and resume text
   */
  private getProfileAndResumeText(): Observable<{
    profile: ProfileDetails | null;
    resumeText: string | null;
    currentOffset: number;
  }> {
    const userId = this.authService.getCurrentUser()?.id;

    if (!userId) {
      console.warn(
        "JobMatcherService: getProfileAndResumeText() - User not authenticated, returning nulls."
      );
      return of({
        profile: null,
        resumeText: null,
        currentOffset: 0,
      });
    }

    return from(
      this.supabaseService
        .getClient()
        .from("users")
        .select("*")
        .eq("id", userId)
        .single()
    ).pipe(
      switchMap(({ data, error }) => {
        if (error || !data) {
          console.error(
            "JobMatcherService: getProfileAndResumeText() - Error fetching user profile:",
            error,
            "Data:",
            data
          );
          return of({
            profile: null,
            resumeText: null,
            currentOffset: 0,
          });
        }

        // Extract preferences from the data
        const preferences = data.preferences || {};

        // Map user data to ProfileDetails
        const profile: ProfileDetails = {
          location: data.location || "",
          seniority: preferences.seniority || "",
          targetRoles: preferences.targetRoles || [],
          extractedText: data.resume_text || "",
        };

        console.log("Profile data loaded:", {
          location: profile.location,
          seniority: profile.seniority,
          targetRoles: profile.targetRoles,
        });

        // Get count of existing matches for offset -- THIS LOGIC IS CHANGING
        // The offset should be based on how many jobs have been processed by OpenAI in this session
        // not necessarily how many are stored in job_matches, as low-scoring ones aren't stored long-term as "presented".
        const currentSessionOffset = this.openAiProcessedCount;
        return of({
          profile,
          resumeText: profile.extractedText || null,
          currentOffset: currentSessionOffset,
        });
      }),
      catchError((err) => {
        console.error(
          "JobMatcherService: Error in getProfileAndResumeText() pipeline:",
          err
        );
        return of({
          profile: null,
          resumeText: null,
          currentOffset: 0,
        });
      })
    );
  }

  /**
   * Take an action on a job (apply, save, decline)
   */
  takeJobAction(
    jobId: string,
    action: "apply" | "save" | "decline"
  ): Observable<{ success: boolean }> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      console.error("Cannot take action, user not authenticated.");
      return of({ success: false });
    }

    // Map actions to appropriate status values
    let statusToSet: string;
    switch (action) {
      case "apply":
        statusToSet = "apply_clicked";
        break;
      case "save":
        statusToSet = "saved"; // Assuming 'saved' is the correct status
        break;
      case "decline":
        statusToSet = "declined"; // Use 'declined' instead of 'decline'
        break;
      default:
        console.error(`Unknown action: ${action}`);
        return of({ success: false }); // Or handle error appropriately
    }

    return from(
      this.supabaseService
        .getClient()
        .from("job_matches")
        .update({
          status: statusToSet,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .eq("job_id", jobId)
    ).pipe(
      map((response) => {
        if (response.error) {
          console.error("Error updating job match status:", response.error);
          return { success: false };
        }

        // Update job matches count after action is complete
        this.updateJobMatchesCount();

        return { success: true };
      }),
      catchError((error) => {
        console.error("Error in takeJobAction pipeline:", error);
        return of({ success: false });
      })
    );
  }

  /**
   * Fetch candidate jobs from DB based on profile, then score them using AI.
   * Fetches the *next available batch* based on offset.
   */
  findAndScoreNextBatch(
    profile: ProfileDetails,
    resumeText: string,
    offset: number
  ): Observable<FindMoreJobMatchesResult> {
    const BATCH_LIMIT = 5;
    const normalizeSeniority = (level: string): string => {
      return level
        .replace(/\s*\([^)]*\)/, "")
        .trim()
        .toLowerCase();
    };
    const profileSeniorities = (profile.seniority || "")
      .split(",")
      .map(normalizeSeniority)
      .filter((s) => s);
    const targetJobLevels: string[] = [];
    profileSeniorities.forEach((ps) => {
      if (ps.includes("entry") || ps.includes("junior"))
        targetJobLevels.push("entry level", "junior");
      if (ps.includes("mid")) targetJobLevels.push("mid-level");
      if (ps.includes("senior")) targetJobLevels.push("senior");
      if (ps.includes("lead")) targetJobLevels.push("lead");
      if (
        ps.includes("manager") ||
        ps.includes("director") ||
        ps.includes("vp") ||
        ps.includes("executive")
      ) {
        targetJobLevels.push("manager", "director", "vp", "executive");
      }
    });
    const uniqueTargetJobLevels = [...new Set(targetJobLevels)];

    return from(
      this.fetchCandidateJobs(
        profile,
        uniqueTargetJobLevels,
        BATCH_LIMIT,
        offset
      )
    ).pipe(
      switchMap((fetchResult: FetchCandidateJobsResult) => {
        if (
          fetchResult.sourceTotallyExhausted &&
          (!fetchResult.candidateBatch ||
            fetchResult.candidateBatch.length === 0)
        ) {
          console.log("No candidate jobs found and source is exhausted.");
          return of({ scoredMatches: [], sourceTotallyExhausted: true });
        }
        if (
          !fetchResult.candidateBatch ||
          fetchResult.candidateBatch.length === 0
        ) {
          // If batch is empty but source isn't exhausted, it means we might have more to process later.
          // For this call, it means no AI scoring happens for *this* batch.
          return of({
            scoredMatches: [],
            sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
          });
        }

        this.openAiProcessedCount += fetchResult.candidateBatch.length; // Increment counter

        const profileWithId = {
          ...profile,
          userId: this.authService.getCurrentUser()?.id,
        };

        return from(
          this.jobMatchingAIService.getJobMatches(
            profileWithId,
            resumeText,
            fetchResult.candidateBatch
          )
        ).pipe(
          switchMap((scoredAimatches: JobMatch[]) => {
            if (!scoredAimatches || scoredAimatches.length === 0) {
              console.log(
                "No new matches were scored above threshold by AI for this batch."
              );
              // Source exhaustion is determined by fetchResult, not by AI scoring outcome
              return of({
                scoredMatches: [],
                sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
              });
            }

            const userId = this.authService.getCurrentUser()?.id;
            if (!userId) {
              console.error("Cannot save matches, user is not authenticated.");
              return of({
                scoredMatches: [],
                sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
              }); // still pass exhaustion status
            }

            console.log(
              `Attempting to save ${scoredAimatches.length} new matches to DB...`
            );
            const matchesToInsert = scoredAimatches.map((match) => ({
              user_id: userId,
              job_id: match.job_id,
              ai_score: match.ai_score,
              ai_reasoning: match.ai_reasoning,
              status: "presented",
            }));

            return from(
              this.supabaseService
                .getClient()
                .from("job_matches")
                .insert(matchesToInsert)
                .select()
            ).pipe(
              map((response) => {
                if (response.error) {
                  console.error("Error inserting job matches:", response.error);
                  return {
                    scoredMatches: [],
                    sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
                  };
                }
                console.log(
                  `Successfully saved ${
                    response.data?.length || 0
                  } new matches.`
                );
                return {
                  scoredMatches: scoredAimatches,
                  sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
                };
              }),
              catchError((insertError) => {
                console.error(
                  "Error during match insertion process:",
                  insertError
                );
                return of({
                  scoredMatches: [],
                  sourceTotallyExhausted: fetchResult.sourceTotallyExhausted,
                });
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error("Error in findAndScoreNextBatch pipeline:", error);
        return of({ scoredMatches: [], sourceTotallyExhausted: true }); // Assume exhaustion on error
      })
    );
  }

  /**
   * Helper to fetch candidate jobs from Supabase based on profile filters.
   */
  private async fetchCandidateJobs(
    profile: ProfileDetails,
    targetJobLevels: string[],
    limit: number,
    offset: number
  ): Promise<FetchCandidateJobsResult> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      console.warn(
        "fetchCandidateJobs: User not authenticated. Cannot fetch matches."
      );
      return {
        candidateBatch: [],
        totalPotentialCount: 0,
        sourceTotallyExhausted: true,
      };
    }

    console.log(
      "JobMatcherService: fetchCandidateJobs called with: profileLocation: ",
      profile.location,
      ", targetJobLevels: [",
      targetJobLevels.join(", "),
      "], targetRoles: [",
      profile.targetRoles?.join(", "),
      "], limit: ",
      limit,
      ", offset: ",
      offset
    );

    try {
      // First, handle location filtering if a location is specified
      const profileLocation = profile.location?.trim().toLowerCase();
      let jobIdsFromLocationQuery: string[] | null = null;

      if (profileLocation && profileLocation !== "not specified") {
        if (profileLocation === "remote") {
          // Handle remote jobs differently - we'll apply this filter later
          console.log("User prefers remote jobs");
        } else {
          // Split location into parts (e.g., "London, United Kingdom" -> ["london", "united kingdom"])
          const locationParts = profileLocation.split(",").map(part => part.trim());
          
          // Query job_locations table first to get matching job IDs
          const locationQueryBuilder = this.supabaseService
            .getClient()
            .from("job_locations")
            .select("job_id");

          // Build location filter conditions based on parts
          if (locationParts.length === 2) {
            // If we have city and country, search for exact matches first
            const cityPart = locationParts[0];
            const countryPart = locationParts[1];
            
            locationQueryBuilder.or(
              `city_geocoded.eq.${cityPart},` +
              `and(city_geocoded.ilike.${cityPart}%,country_geocoded.ilike.${countryPart}%)`
            );
          } else {
            // If we have a single term, search in all fields
            const searchTerm = locationParts[0];
            locationQueryBuilder.or(
              `city_geocoded.eq.${searchTerm},` +
              `country_geocoded.eq.${searchTerm},` +
              `city_geocoded.ilike.${searchTerm}%,` +
              `country_geocoded.ilike.${searchTerm}%`
            );
          }

          const { data: locationData, error: locationError } = await locationQueryBuilder;

          if (locationError) {
            console.error("Error fetching job_ids from job_locations:", locationError);
            return {
              candidateBatch: [],
              totalPotentialCount: 0,
              sourceTotallyExhausted: true,
            };
          }

          jobIdsFromLocationQuery = locationData
            ? [...new Set(locationData.map((loc: any) => loc.job_id))]
            : [];

          console.log(`Found ${jobIdsFromLocationQuery.length} jobs matching location: ${profileLocation}`);

          if (jobIdsFromLocationQuery.length === 0) {
            console.log(`No jobs found matching location: ${profileLocation}`);
            return {
              candidateBatch: [],
              totalPotentialCount: 0,
              sourceTotallyExhausted: true,
            };
          }
        }
      }

      // Get existing matches to exclude
      const { data: existingMatchData, error: matchError } =
        await this.supabaseService
          .getClient()
          .from("job_matches")
          .select("job_id")
          .eq("user_id", userId);

      if (matchError) {
        console.error("Error fetching existing job matches:", matchError);
        return {
          candidateBatch: [],
          totalPotentialCount: 0,
          sourceTotallyExhausted: true,
        };
      }

      const existingJobIds =
        existingMatchData?.map((match) => match.job_id) || [];
      console.log(
        `Found ${existingJobIds.length} existing job match IDs to exclude.`
      );

      // Start building the main jobs query
      let query = this.supabaseService
        .getClient()
        .from("jobs")
        .select("*, job_locations(*)", { count: "exact" }) // Request count for all potential
        .eq("status", "published") // Only match published jobs
        .eq("is_expired", false); // Exclude jobs reported as no longer available

      // Build filter conditions using PostgREST syntax
      let filterConditions = [];

      // Add job level filters
      if (targetJobLevels.length > 0) {
        const levelConditions = targetJobLevels.map(
          (level) => `level.ilike.%${level.toLowerCase()}%`
        );
        filterConditions.push(`or(${levelConditions.join(",")})`);
      }

      // Add target role filters
      if (profile.targetRoles && profile.targetRoles.length > 0) {
        const roleConditions = profile.targetRoles.map(
          (role) => `title.ilike.%${role.trim()}%`
        );
        filterConditions.push(`or(${roleConditions.join(",")})`);
      }

      // Combine all filters with AND
      if (filterConditions.length > 0) {
        query = query.or(`and(${filterConditions.join(",")})`);
      }

      // Apply location filtering
      if (profileLocation === "remote") {
        query = query.eq("work_type", "remote");
      } else if (jobIdsFromLocationQuery) {
        query = query.in("id", jobIdsFromLocationQuery);
      }

      // Exclude existing matches
      if (existingJobIds.length > 0) {
        query = query.not("id", "in", `(${existingJobIds.join(",")})`);
      }

      // Order by created date
      query = query.order("created_at", { ascending: false });

      // Execute the query
      const {
        data: allPotentialCandidates,
        error: allCandidatesError,
        count: totalPotentialCount,
      } = await query;

      if (allCandidatesError) {
        console.error(
          "Error fetching ALL potential candidate jobs:",
          allCandidatesError
        );
        return {
          candidateBatch: [],
          totalPotentialCount: 0,
          sourceTotallyExhausted: true,
        };
      }

      const potentialCount = totalPotentialCount || 0;

      console.log(
        "Total potential candidates after filtering:",
        allPotentialCandidates?.length || 0
      );

      // Apply limit and offset to the retrieved list for the current batch
      const batchedCandidates =
        allPotentialCandidates?.slice(offset, offset + limit) || [];

      console.log(
        "Fetched candidate jobs raw data (for current batch from all potential):",
        batchedCandidates
      );
      console.log(
        "Fetched candidate jobs count (for current batch):",
        batchedCandidates?.length || 0,
        "results"
      );

      const sourceExhausted = offset >= potentialCount;

      return {
        candidateBatch: batchedCandidates || [],
        totalPotentialCount: potentialCount,
        sourceTotallyExhausted: sourceExhausted,
      };
    } catch (error) {
      console.error("Error in fetchCandidateJobs:", error);
      return {
        candidateBatch: [],
        totalPotentialCount: 0,
        sourceTotallyExhausted: true,
      };
    }
  }

  /**
   * Find job matches based on the user's resume
   */
  findJobMatches(
    tempResumeId: string,
    profileDetails: any
  ): Observable<JobMatch[]> {
    // Use mock data if in development mode
    if (this.useMockData) {
      console.log("Using mock job matcher service");
      console.log("Resume ID:", tempResumeId);
      console.log("Profile details:", profileDetails);

      // Generate some mock job matches
      const mockJobs: JobMatch[] = [
        {
          match_id: "match-1",
          job_id: "job-1",
          user_id: "user-1",
          session_id: "session-1",
          ai_score: 92,
          ai_reasoning: "Good match based on UX design skills",
          status: "presented",
          created_at: new Date().toISOString(),
          job: {
            id: "job-1",
            title: "Senior UX Designer",
            company: "TechCorp",
            location: profileDetails.location || "San Francisco, CA",
            employment_type: "Full-time",
            level: "Senior",
            category: "Design",
            work_type: WorkType.Remote,
            salary_min: 120000,
            salary_max: 150000,
            short_description:
              "We're looking for a senior UX designer to join our product team...",
            about_company: "TechCorp is a leading technology company...",
            responsibilities: [
              "Lead UX design initiatives",
              "Collaborate with product managers",
            ],
            requirements: [
              "5+ years of UX design experience",
              "Figma proficiency",
            ],
            benefits: ["Flexible work hours", "Health insurance"],
            application_url: "https://example.com/apply/job-1",
            logo_img: "https://via.placeholder.com/150",
          },
        },
        {
          match_id: "match-2",
          job_id: "job-2",
          user_id: "user-1",
          session_id: "session-1",
          ai_score: 87,
          ai_reasoning: "Good match based on product design skills",
          status: "presented",
          created_at: new Date().toISOString(),
          job: {
            id: "job-2",
            title: "Product Designer",
            company: "DesignHub",
            location: profileDetails.location || "San Francisco, CA",
            employment_type: "Full-time",
            level: "Mid-level",
            category: "Design",
            work_type: WorkType.Remote,
            salary_min: 100000,
            salary_max: 130000,
            short_description:
              "Join our team creating beautiful and functional products...",
            about_company: "DesignHub is a design-focused startup...",
            responsibilities: [
              "Create user interfaces",
              "Build design systems",
            ],
            requirements: [
              "3+ years of product design experience",
              "Design systems experience",
            ],
            benefits: ["Remote work", "Professional development budget"],
            application_url: "https://example.com/apply/job-2",
            logo_img: "https://via.placeholder.com/150",
          },
        },
        {
          match_id: "match-3",
          job_id: "job-3",
          user_id: "user-1",
          session_id: "session-1",
          ai_score: 79,
          ai_reasoning: "Good match based on UI design skills",
          status: "presented",
          created_at: new Date().toISOString(),
          job: {
            id: "job-3",
            title: "UX/UI Designer",
            company: "Creative Inc.",
            location: profileDetails.location || "San Francisco, CA",
            employment_type: "Full-time",
            level: "Mid-level",
            category: "Design",
            work_type: WorkType.Remote,
            salary_min: 90000,
            salary_max: 120000,
            short_description:
              "We need a talented designer to help us reimagine our product...",
            about_company: "Creative Inc. is a design agency...",
            responsibilities: [
              "Design user interfaces",
              "Conduct user testing",
            ],
            requirements: [
              "2+ years of UI design experience",
              "Figma or Adobe XD proficiency",
            ],
            benefits: ["Flexible schedule", "Creative work environment"],
            application_url: "https://example.com/apply/job-3",
            logo_img: "https://via.placeholder.com/150",
          },
        },
      ];

      // Return mock data with a delay to simulate network request
      return of(mockJobs).pipe(delay(1500));
    }

    // Real implementation for production
    const headers = this.getAuthHeaders();

    return this.http.post<JobMatch[]>(
      `${this.API_URL}/ai-matcher/matches`,
      {
        tempResumeId,
        profileDetails,
      },
      { headers }
    );
  }

  /**
   * Get matched jobs for the dashboard
   * @param userId The ID of the user to filter by
   * @param status Optional status to filter by
   */
  getMatchedJobs(
    userId: string,
    status?: "saved" | "apply_clicked" | "presented" | "declined"
  ): Observable<JobMatch[]> {
    if (!userId) {
      return of([]);
    }

    let query = this.supabaseService
      .getClient()
      .from("job_matches")
      .select(
        `
        match_id,
        job_id,
        user_id,
        ai_score,
        ai_reasoning,
        status,
        created_at,
        job:jobs!inner (*, job_locations(*))
      `
      )
      .eq("user_id", userId)
      .eq("job.status", "published"); // Only show matches for published jobs

    if (status) {
      query = query.eq("status", status);
    }

    query = query.order("created_at", { ascending: false });

    return from(query).pipe(
      map(({ data, error }) => {
        if (error) {
          console.error(
            `Error fetching matched jobs for status ${status}:`,
            error
          );
          throw error;
        }

        const jobMatches = (data || []).map((match: any) => {
          const job = match.job as Job;
          if (job) {
            job.display_location = this.formatDisplayLocation(job);
          }
          return {
            match_id: match.match_id,
            job_id: match.job_id,
            user_id: match.user_id,
            session_id: `session-${match.user_id}-${match.job_id}`,
            ai_score: match.ai_score,
            ai_reasoning: match.ai_reasoning,
            status: match.status,
            created_at: match.created_at,
            job: job,
          } as JobMatch;
        });

        // Always update the count when fetching presented jobs
        if (status === "presented") {
          this._jobMatchesCount.next(jobMatches.length);
        }
        return jobMatches;
      }),
      catchError((err) => {
        console.error(
          `JobMatcherService: Error in getMatchedJobs pipeline for status ${status}:`,
          err
        );
        if (status === "presented") {
          this._jobMatchesCount.next(0);
        }
        return of([]);
      })
    );
  }

  private _formatSingleLocation(loc: JobLocation): string {
    if (loc.city_geocoded && loc.country_geocoded) {
      return `${loc.city_geocoded}, ${loc.country_geocoded}`;
    }
    if (loc.city_geocoded) {
      return loc.city_geocoded;
    }
    if (loc.country_geocoded) {
      return loc.country_geocoded;
    }
    // Fallback for various scenarios, including specific raw_location_string for remote
    // or when geocoding is partial/missing for on-site/hybrid.
    if (
      loc.raw_location_string &&
      loc.raw_location_string.toLowerCase() !== "remote"
    ) {
      return loc.raw_location_string;
    }
    return ""; // Should ideally not happen if loc has some data
  }

  private formatDisplayLocation(job: Job): string {
    if (!job) return "";

    const locations = job.job_locations || [];
    const workType = job.work_type;

    if (workType === WorkType.Remote) {
      if (locations.length === 0) {
        return "Remote";
      }
      const specificGeocodedLocations = locations.filter(
        (loc) =>
          loc.city_geocoded ||
          loc.country_geocoded ||
          (loc.raw_location_string &&
            loc.raw_location_string.toLowerCase() !== "remote")
      );

      if (specificGeocodedLocations.length === 0) {
        return "Remote";
      } else if (specificGeocodedLocations.length === 1) {
        const loc = specificGeocodedLocations[0];
        const detail = this._formatSingleLocation(loc);
        return detail ? `Remote (${detail})` : "Remote";
      } else {
        // Multiple specific geocoded locations for Remote
        const locationStrings = specificGeocodedLocations
          .map((loc) => this._formatSingleLocation(loc))
          .filter((str) => str);
        return locationStrings.length > 0
          ? `Remote (${locationStrings.join("; ")})`
          : "Remote";
      }
    }

    // For On-site or Hybrid jobs
    if (locations.length === 0) {
      return job.location || "";
    }

    if (locations.length === 1) {
      return this._formatSingleLocation(locations[0]) || job.location || "";
    }

    // Multiple geocoded locations for On-site/Hybrid jobs
    const locationStrings = locations
      .map((loc) => this._formatSingleLocation(loc))
      .filter((str) => str);
    return locationStrings.join("; ") || job.location || "";
  }

  /**
   * Save a job to the user's dashboard
   */
  saveJob(jobId: string): Observable<{ success: boolean }> {
    // TODO: Add similar async user check if needed
    const headers = this.getAuthHeaders();
    // ... rest of original saveJob implementation using HTTP POST ...
    return this.http.post<{ success: boolean }>(
      `${this.API_URL}/dashboard/save-job`,
      { jobId },
      { headers }
    );
  }

  /**
   * Track when a user clicks 'Apply'
   */
  trackApplyClick(jobId: string): Observable<{ success: boolean }> {
    // TODO: Add similar async user check if needed
    const headers = this.getAuthHeaders();
    // ... rest of original trackApplyClick implementation using HTTP POST ...
    return this.http.post<{ success: boolean }>(
      `${this.API_URL}/dashboard/track-apply`,
      { jobId },
      { headers }
    );
  }

  /**
   * Helper to get headers with Authorization token if available
   */
  private getAuthHeaders(): HttpHeaders {
    // This method implicitly relies on the session being ready when called.
    // It might need adjustment if called very early in other contexts.
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    const token = this.authService.getAccessToken(); // Uses synchronous call
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  }

  /**
   * Check if a user has run the job matcher before
   * @param userId The ID of the user to check
   * @returns Observable<boolean> indicating if the user has run the matcher
   */
  hasUserRunMatcher(userId: string): Observable<boolean> {
    return from(
      this.supabaseService
        .getClient()
        .from("job_matches")
        .select("count", { count: "exact", head: true })
        .eq("user_id", userId)
    ).pipe(
      map((response) => {
        if (response.error) {
          console.error("Error checking job matcher status:", response.error);
          return false;
        }
        return (response.count || 0) > 0;
      }),
      catchError((error) => {
        console.error("Error in hasUserRunMatcher:", error);
        return of(false);
      })
    );
  }

  // Reset the OpenAI processed count
  resetOpenAiProcessedCount(): void {
    this.openAiProcessedCount = 0;
  }

  // Get the OpenAI processed count
  getOpenAiProcessedCount(): number {
    return this.openAiProcessedCount;
  }
}
